import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { useConfiguration } from '../../context/configurationContext';
import { useWalletSession } from '../../context/WalletSessionContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import {
  progressiveAuthIntents,
  progressiveAuthShellCopy,
} from '../../config/progressiveAuthCopy';
import { isCommsOnly } from '../../config/commsMode';
import { authenticationInProgress, login, signup } from '../../ducks/auth.duck';
import { createResourceLocatorString } from '../../util/routes';
import { isUserAuthorized } from '../../util/userHelpers';
import { getHandleSubmitSignup } from '../../containers/AuthenticationPage/AuthenticationPage.helpers';
import LoginForm from '../../containers/AuthenticationPage/LoginForm/LoginForm';
import SignupForm from '../../containers/AuthenticationPage/SignupForm/SignupForm';
import TermsAndConditions from '../../containers/AuthenticationPage/TermsAndConditions/TermsAndConditions';
import Web3WalletAuthPanel from '../../containers/AuthenticationPage/Web3WalletAuthPanel/Web3WalletAuthPanel';

import css from './ProgressiveAuthGate.module.css';

const ProgressiveAuthInlinePanel = ({ intent, userType, returnTo, onComplete }) => {
  const config = useConfiguration();
  const intl = useIntl();
  const dispatch = useDispatch();
  const { authMode } = useWalletSession();
  const intentConfig = progressiveAuthIntents[intent] || progressiveAuthIntents.general;
  const [tab, setTab] = useState(intentConfig.defaultTab);
  const authInProgress = useSelector(state => authenticationInProgress(state));
  const isAuthenticated = useSelector(state => state.auth?.isAuthenticated);
  const currentUser = useSelector(state => state.user?.currentUser);
  const loginError = useSelector(state => state.auth?.loginError);
  const signupError = useSelector(state => state.auth?.signupError);

  const { userTypes = [], userFields = [] } = config.user;
  const preselectedUserType =
    userType ||
    intentConfig.userType ||
    userTypes.find(ut => ut.userType === 'customer')?.userType ||
    null;

  const submitLogin = useCallback(
    ({ email, password }) => dispatch(login(email, password)),
    [dispatch]
  );
  const submitSignup = useCallback(params => dispatch(signup(params)), [dispatch]);

  useEffect(() => {
    if (isAuthenticated && isUserAuthorized(currentUser)) {
      onComplete();
    }
  }, [isAuthenticated, currentUser, onComplete]);

  useEffect(() => {
    if (authMode === 'web3' && intentConfig.walletOnlyOk) {
      onComplete();
    }
  }, [authMode, intentConfig.walletOnlyOk, onComplete]);

  useEffect(() => {
    if (authMode === 'web3' && !intentConfig.walletOnlyOk) {
      setTab('signup');
    }
  }, [authMode, intentConfig.walletOnlyOk]);

  const termsAndConditions = (
    <TermsAndConditions
      intl={intl}
      onOpenTermsOfService={() => {}}
      onOpenPrivacyPolicy={() => {}}
    />
  );

  const tabContent =
    tab === 'wallet' ? (
      <Web3WalletAuthPanel
        embedded
        from={returnTo}
        onAuthenticated={onComplete}
        className={css.walletPanel}
      />
    ) : tab === 'login' ? (
      <>
        {loginError ? <p className={css.formError}>{loginError.message}</p> : null}
        <LoginForm className={css.compactForm} onSubmit={submitLogin} inProgress={authInProgress} />
      </>
    ) : (
      <>
        {signupError ? <p className={css.formError}>{signupError.message}</p> : null}
        <SignupForm
          className={css.compactForm}
          onSubmit={getHandleSubmitSignup({ submitSignup, userFields, userTypes })}
          inProgress={authInProgress}
          termsAndConditions={termsAndConditions}
          preselectedUserType={preselectedUserType}
          userTypes={userTypes}
          userFields={userFields}
        />
      </>
    );

  const walletConnectedNeedsEmail =
    authMode === 'web3' && !intentConfig.walletOnlyOk && tab !== 'wallet';

  const showWalletTab = intentConfig.walletOnlyOk || !isCommsOnly();

  return (
    <div className={css.inlinePanel}>
      <div className={css.tabs}>
        <button
          type="button"
          className={classNames(css.tab, tab === 'signup' ? css.tabActive : null)}
          onClick={() => setTab('signup')}
        >
          {progressiveAuthShellCopy.signupTab}
        </button>
        <button
          type="button"
          className={classNames(css.tab, tab === 'login' ? css.tabActive : null)}
          onClick={() => setTab('login')}
        >
          {progressiveAuthShellCopy.loginTab}
        </button>
        {showWalletTab ? (
          <button
            type="button"
            className={classNames(css.tab, tab === 'wallet' ? css.tabActive : null)}
            onClick={() => setTab('wallet')}
          >
            {progressiveAuthShellCopy.walletTab}
          </button>
        ) : null}
      </div>

      {walletConnectedNeedsEmail ? (
        <p className={css.walletConnectedNote}>{progressiveAuthShellCopy.walletConnectedNeedsEmail}</p>
      ) : null}

      {tabContent}

      <p className={css.protectionNote}>{progressiveAuthShellCopy.protectionNote}</p>
    </div>
  );
};

const ProgressiveAuthGate = ({ request, onComplete, onDismiss }) => {
  const routeConfiguration = useRouteConfiguration();
  const intentConfig =
    progressiveAuthIntents[request.intent] || progressiveAuthIntents.general;
  const returnTo = request.returnTo || null;

  const fullAuthUrl = useMemo(() => {
    const routeName = request.userType ? 'SignupForUserTypePage' : 'SignupPage';
    const params = request.userType ? { userType: request.userType } : {};
    return createResourceLocatorString(routeName, routeConfiguration, params, {});
  }, [request.userType, routeConfiguration]);

  return (
    <div
      className={css.overlay}
      role="dialog"
      aria-modal="true"
      aria-labelledby="progressive-auth-title"
      data-testid="progressive-auth-gate"
    >
      <div className={css.modal}>
        <header className={css.header}>
          <h2 id="progressive-auth-title" className={css.title}>
            {intentConfig.title}
          </h2>
          <p className={css.body}>{intentConfig.body}</p>
        </header>

        <ProgressiveAuthInlinePanel
          intent={request.intent}
          userType={request.userType || intentConfig.userType}
          returnTo={returnTo}
          onComplete={onComplete}
        />

        <footer className={css.footer}>
          <button type="button" className={css.dismiss} onClick={onDismiss}>
            {progressiveAuthShellCopy.dismiss}
          </button>
          <Link
            to={{ pathname: fullAuthUrl, state: returnTo ? { from: returnTo } : undefined }}
            className={css.fullPageLink}
            onClick={onDismiss}
          >
            {progressiveAuthShellCopy.fullPageLink}
          </Link>
        </footer>
      </div>
    </div>
  );
};

export default ProgressiveAuthGate;
